import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useMutation } from "@apollo/client";

import { TriviaGame } from "../../../../../../../../graphql/helpers/isTriviaGame";
import { Section } from "../../../Common";
import { useProject } from "../../../../../../../../contexts/project/ProjectContext";
import {
  TRIVIA_QUESTION_CREATE,
  TRIVIA_QUESTION_DELETE,
  TRIVIA_QUESTION_UPDATE,
} from "../../../../../../../../graphql/mutations/TriviaQuestionMutation";
import { FileEditAltIcon, IconButton, notification, PlusIcon, RichText } from "@datagame/ui";
import { GAME_ALL } from "../../../../../../../../graphql/queries/GameQueries";
import { AttributeElementRich } from "./AttributeElementRich";

import styles from "./QuestionsSection.module.scss";

export interface QuestionsSectionProps {
  game: TriviaGame;
}

export const QuestionsSection = ({ game }: QuestionsSectionProps) => {
  const { t } = useTranslation("trivia");
  const { project: { id: projectId } = {} } = useProject();

  const [question, setQuestion] = useState<string | undefined>();

  const [createQuestion] = useMutation(TRIVIA_QUESTION_CREATE, {
    onCompleted: () => {
      setQuestion("");
    },
    onError: (error) => {
      notification.danger({
        title: t("common:error"),
        description: error.message,
      });
    },
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });

  const [deleteQuestion] = useMutation(TRIVIA_QUESTION_DELETE, {
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
    onError: (error) => {
      notification.danger({
        title: t("common:error"),
        description: error.message,
      });
    },
  });

  const [updateQuestion] = useMutation(TRIVIA_QUESTION_UPDATE, {
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });

  const handleAddAttribute = async () => {
    await createQuestion({
      variables: {
        data: {
          question,
          options: [
            {
              text: "Question 1",
              isCorrect: true,
              points: 1,
            },
            {
              text: "Question 2",
              isCorrect: false,
              points: 1,
            },
            {
              text: "Question 3",
              isCorrect: false,
              points: 1,
            },
            {
              text: "Question 4",
              isCorrect: false,
              points: 1,
            },
          ],
        },
        triviaConfigurationId: game.configuration.id,
      },
    });
  };

  const handleDeleteAttribute = async (id: string) => {
    try {
      await deleteQuestion({
        variables: {
          triviaQuestionId: id,
        },
      });
    } catch (error) {
      if ((error as Error).message.includes("attributeId_fkey")) {
        return notification.danger({
          title: t(`project:gameSettings.form.errors.attributes.title.card`),
          description: t(`project:gameSettings.form.errors.attributes.deleteActive.card`),
        });
      }
      throw error;
    }
  };

  const handleUpdateAttribute = (id: string, newQuestion: string) => {
    updateQuestion({
      variables: {
        triviaQuestionId: id,
        data: {
          question: newQuestion,
        },
      },
    });
  };

  const handleAttributeNameChange = (value: string) => {
    setQuestion(value);
  };

  return (
    <Section title={t("gameSettings.questionsSection")} tip={t("gameSettings.questionsTip")}>
      {game.configuration.questions.map((attribute) => {
        return (
          <AttributeElementRich
            key={attribute.id}
            attribute={attribute}
            value={attribute.question}
            onDelete={handleDeleteAttribute}
            onEdit={handleUpdateAttribute}
          />
        );
      })}

      <div className={styles.attributeField}>
        <div className={styles.inputWrapper}>
          <RichText
            size="small"
            icon={<FileEditAltIcon />}
            value={question}
            onChange={handleAttributeNameChange}
            placeholder={t("gameSettings.questionsPlaceholder") as string}
          />

          <IconButton
            icon={<PlusIcon />}
            style="outline"
            className={styles.attributeAdd}
            onClick={handleAddAttribute}
          />
        </div>
      </div>
    </Section>
  );
};
